import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import AuctionCard from "@/components/AuctionCard";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  ArrowRight, ShieldCheck, TrendUp, Headset, Storefront, Package, HandCoins,
  Star, Lightning, ClockCounterClockwise, Fire, Article, Trophy, MapPin,
  Sparkle, MagnifyingGlass,
  House, Car, Wrench, ShoppingBag, Factory, Toolbox, GraduationCap, Briefcase, PawPrint, HandHeart,
} from "@phosphor-icons/react";

// Map DB icon string (Phosphor name) → component for category tiles
const CAT_ICON_MAP = {
  House, Car, Wrench, ShoppingBag, Factory, Toolbox,
  GraduationCap, Briefcase, PawPrint, HandHeart, HandCoins, Storefront,
  Package, Trophy, Sparkle, Star,
};

const HERO_IMG = "https://images.unsplash.com/photo-1620625515032-6ed0c1790c75?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function Home() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [vitrin, setVitrin] = useState([]);
  const [endingSoon, setEndingSoon] = useState([]);
  const [mostBids, setMostBids] = useState([]);
  const [urgent, setUrgent] = useState([]);
  const [newest, setNewest] = useState([]);
  const [trustedStores, setTrustedStores] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [growth, setGrowth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [favSet, setFavSet] = useState(new Set());

  useEffect(() => {
    if (!user) { setFavSet(new Set()); return; }
    api.get("/me/favorites").then(({ data }) => setFavSet(new Set(data.map((f) => f.id)))).catch(() => {});
  }, [user]);

  const toggleFav = async (id) => {
    if (!user) { toast.error("Favori için giriş yapın"); nav("/giris"); return; }
    const isFav = favSet.has(id);
    const next = new Set(favSet);
    if (isFav) next.delete(id); else next.add(id);
    setFavSet(next);
    try {
      if (isFav) await api.delete(`/favorites/${id}`);
      else await api.post(`/favorites/${id}`);
    } catch {
      setFavSet(favSet);
      toast.error("Favori güncellenemedi");
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const [v, es, mb, ur, nw, ts, bl, cat] = await Promise.all([
          api.get("/auctions", { params: { doping: "homepage_vitrin", status: "active", limit: 8 } }),
          api.get("/auctions", { params: { ending_soon: true, status: "active", limit: 8 } }),
          api.get("/auctions", { params: { status: "active", sort: "most_bids", limit: 8 } }),
          api.get("/auctions", { params: { doping: "urgent_badge", status: "active", limit: 6 } }),
          api.get("/auctions", { params: { status: "active", sort: "newest", limit: 8 } }),
          api.get("/stores/trusted", { params: { limit: 4 } }),
          api.get("/blog"),
          api.get("/categories"),
        ]);
        setVitrin(v.data);
        setEndingSoon(es.data);
        setMostBids(mb.data);
        setUrgent(ur.data);
        setNewest(nw.data);
        setTrustedStores(ts.data);
        setBlogs(bl.data.slice(0, 3));
        setCategories(cat.data);
        // Homepage badge from active listings
        setGrowth({ active_count: nw.data.length });
      } finally { setLoading(false); }
    })();
  }, []);

  // Featured strip: use vitrin if available, else best-bid ones
  const featured = (vitrin.length > 0
    ? vitrin
    : [...mostBids]
  ).slice(0, 4);

  return (
    <div data-testid="home-page">
      {/* ============ HERO ============ */}
      <section className="bg-white">
        <div className="max-w-[1280px] mx-auto px-6 pt-20 pb-24 lg:pt-32 lg:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24 items-center">
          <div className="lg:col-span-7 space-y-10">
            <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] font-medium text-slate-500">
              <span className="live-dot" />
              Türkiye&apos;nin gerçek zamanlı açık artırma platformu
            </span>
            <h1 className="font-display text-5xl md:text-6xl lg:text-[76px] leading-[1.02] tracking-[-0.03em] text-slate-900 font-medium">
              Elindekileri, <br />
              değerinde satmanın <br />
              <span className="italic text-slate-500">en kolay yolu.</span>
            </h1>
            <p className="text-base md:text-lg text-slate-500 max-w-xl leading-relaxed">
              kolaysatis.com ile açık artırma başlat, dükkanını aç, teklif topla. Gerçek zamanlı, şeffaf ve güvenli.
            </p>

            {/* Hero search — primary CTA */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const v = e.currentTarget.elements.q?.value?.trim();
                nav(v ? `/kesfet?q=${encodeURIComponent(v)}` : "/kesfet");
              }}
              className="flex items-center bg-white premium-border rounded-full soft-shadow p-1.5 max-w-xl"
              data-testid="hero-search-form"
            >
              <div className="flex-1 flex items-center pl-4">
                <MagnifyingGlass size={17} className="text-slate-400 mr-2" />
                <input
                  name="q"
                  data-testid="hero-search-input"
                  placeholder="Ne aramıştın? Örn: iPhone 14, Rolex, MacBook..."
                  className="w-full bg-transparent outline-none text-sm md:text-base placeholder:text-slate-400 text-slate-900 py-2.5"
                />
              </div>
              <button
                type="submit"
                data-testid="hero-search-submit"
                className="bg-slate-900 text-white text-sm font-medium px-5 md:px-6 py-2.5 rounded-full hover:bg-slate-800 transition-colors flex items-center gap-1.5 shrink-0"
              >
                Ara
              </button>
            </form>

            <div className="flex flex-wrap gap-3 pt-1">
              <Link to="/kesfet" data-testid="hero-explore-btn"
                className="inline-flex items-center gap-2 bg-white text-slate-900 premium-border hover:border-slate-900 px-5 py-2.5 text-sm font-medium rounded-full transition-colors">
                Keşfet <ArrowRight size={14} weight="bold" />
              </Link>
              <Link to="/ilan-ver" data-testid="hero-sell-btn"
                className="inline-flex items-center gap-2 bg-slate-900 text-white hover:bg-slate-800 px-5 py-2.5 text-sm font-medium rounded-full transition-colors">
                Hemen sat
              </Link>
            </div>

            <div className="flex flex-wrap gap-x-14 gap-y-3 pt-8">
              {[
                { n: "10.000+", l: "aktif üye" },
                { n: "%99", l: "memnuniyet" },
                { n: "7/24", l: "canlı destek" },
              ].map((s) => (
                <div key={s.l}>
                  <p className="font-mono-tight text-2xl text-slate-900 tracking-tight">{s.n}</p>
                  <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400 mt-1">{s.l}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative rounded-2xl overflow-hidden soft-shadow-lg">
              <img src={HERO_IMG} alt="Rolex açık artırması" className="w-full h-[440px] md:h-[560px] object-cover" />
              <div className="absolute top-5 left-5 flex items-center gap-2 bg-white/85 backdrop-blur-md px-3 py-1.5 rounded-full">
                <span className="live-dot" />
                <span className="text-[10px] uppercase tracking-[0.24em] font-medium text-[#B54037]">Canlı</span>
              </div>
              <div className="absolute bottom-5 left-5 right-5 bg-white/85 backdrop-blur-md rounded-xl p-4">
                <p className="text-[10px] uppercase tracking-[0.24em] font-medium text-slate-500">1970 Rolex Submariner</p>
                <div className="flex items-end justify-between mt-3">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.22em] font-medium text-slate-400">Güncel</p>
                    <p className="font-mono-tight text-2xl font-medium text-slate-900 tracking-tight">₺52.000</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] uppercase tracking-[0.22em] font-medium text-slate-400">Kalan</p>
                    <p className="font-mono-tight text-xl font-medium text-[#B54037] tracking-tight">02:34:11</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ TRUST BAND — right after hero for immediate confidence ============ */}
      <section className="border-t divider bg-slate-50/40" data-testid="hero-trust-band">
        <div className="max-w-[1280px] mx-auto px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
          {[
            { icon: ShieldCheck, label: "TCKN doğrulamalı üyelik" },
            { icon: HandCoins, label: "Havuz hesap güvencesi" },
            { icon: Lightning, label: "Canlı gerçek zamanlı teklif" },
            { icon: Headset, label: "7/24 kullanıcı desteği" },
          ].map((t) => (
            <div key={t.label} className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-white premium-border flex items-center justify-center shrink-0">
                <t.icon size={16} weight="regular" className="text-slate-700" />
              </div>
              <p className="text-xs md:text-sm text-slate-600 leading-snug">{t.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ============ VITRIN — Editorial featured strip ============ */}
      <Section
        eyebrow="Öne çıkanlar"
        title="Günün seçkileri"
        testId="section-vitrin"
        right={<Link to="/kesfet?doping_only=1" className="text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors">Tüm vitrin ilanları →</Link>}
      >
        {featured.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8" data-testid="home-vitrin-grid">
            {featured.map((a) => <VitrinCard key={a.id} auction={a} />)}
            {Array.from({ length: Math.max(0, 4 - featured.length) }).map((_, i) => (
              <VitrinAdSlot key={`ad-${i}`} idx={i} />
            ))}
          </div>
        ) : (
          <EmptyBlock
            testid="vitrin-empty"
            icon={Star}
            title="Vitrin bekleniyor"
            copy="Bugün henüz vitrinde ilan yok. Sen ilk vitrini alarak öne çıkabilirsin."
            cta="Doping paketlerini gör"
            href="/panelim"
          />
        )}
      </Section>

      {/* ============ ACİL SATIŞLAR ============ */}
      <Section
        eyebrow="Acil satışlar"
        eyebrowIcon={<Lightning size={12} weight="fill" className="text-[#B54037]" />}
        eyebrowColor="text-[#B54037]"
        title="Bugün acil el değiştirmek isteyenler"
        testId="section-urgent"
        right={<Link to="/kesfet?doping=urgent_badge" className="text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors">Hepsini gör →</Link>}
      >
        {urgent.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="home-urgent-grid">
            {urgent.slice(0, 6).map((a) => <AuctionCard key={a.id} auction={a} onFav={toggleFav} isFav={favSet.has(a.id)} />)}
          </div>
        ) : (
          <EmptyBlock
            testid="urgent-empty"
            icon={Lightning}
            title="Şu an acil satış yok"
            copy="Acil satışlar buraya gelir. Sen de ilanına Acil rozeti ekleyerek hızlı sat."
            cta="İlan oluştur"
            href="/ilan-ver"
          />
        )}
      </Section>

      {/* ============ BİTMEK ÜZERE OLANLAR ============ */}
      {endingSoon.length > 0 && (
        <Section
          eyebrow="Süresi biten"
          eyebrowIcon={<ClockCounterClockwise size={12} weight="bold" />}
          title="Bitmek üzere olanlar"
          testId="section-ending-soon"
          right={<Link to="/kesfet?ending_soon=1" className="text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors">Hepsini gör →</Link>}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="home-ending-soon-grid">
            {endingSoon.slice(0, 4).map((a) => <AuctionCard key={a.id} auction={a} onFav={toggleFav} isFav={favSet.has(a.id)} />)}
          </div>
        </Section>
      )}

      {/* ============ EN ÇOK TEKLİF ALANLAR ============ */}
      {mostBids.length > 0 && (
        <Section
          eyebrow="Kızışan mezatlar"
          eyebrowIcon={<Fire size={12} weight="fill" className="text-slate-500" />}
          title="En çok teklif alanlar"
          testId="section-most-bids"
          right={<Link to="/kesfet?sort=most_bids" className="text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors">Sıralamayı gör →</Link>}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="home-most-bids-grid">
            {mostBids.slice(0, 4).map((a) => <AuctionCard key={a.id} auction={a} onFav={toggleFav} isFav={favSet.has(a.id)} />)}
          </div>
        </Section>
      )}

      {/* ============ POPÜLER KATEGORİLER ============ */}
      {categories.length > 0 && (
        <Section
          eyebrow="Kategoriler"
          title="Popüler koleksiyonlar"
          testId="section-categories"
        >
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="home-categories-grid">
            {categories.slice(0, 10).map((c) => {
              const Icon = CAT_ICON_MAP[c.icon] || Package;
              return (
                <Link
                  key={c.id}
                  to={`/kesfet/${c.id}`}
                  data-testid={`cat-tile-${c.id}`}
                  className="group p-6 rounded-2xl bg-white premium-border hover-lift transition-all duration-500 flex flex-col items-start gap-4 min-h-[152px]"
                >
                  <div className="w-11 h-11 rounded-full bg-slate-50 group-hover:bg-slate-900 transition-colors flex items-center justify-center">
                    <Icon size={20} weight="regular" className="text-slate-700 group-hover:text-white transition-colors" />
                  </div>
                  <p className="font-display text-base font-medium tracking-tight text-slate-900 leading-snug">
                    {c.name}
                  </p>
                  <span className="text-[11px] text-slate-400 group-hover:text-slate-700 transition-colors mt-auto inline-flex items-center gap-1">
                    Keşfet <ArrowRight size={10} weight="bold" />
                  </span>
                </Link>
              );
            })}
          </div>
        </Section>
      )}

      {/* ============ GÜVENİLİR MAĞAZALAR ============ */}
      <Section
        eyebrow="Onaylı mağazalar"
        eyebrowIcon={<Storefront size={12} weight="regular" />}
        title="Güvenilir satıcılar"
        testId="section-trusted-stores"
        right={<Link to="/kesfet" className="text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors">Tüm mağazalar →</Link>}
      >
        {trustedStores.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="home-trusted-stores-grid">
            {trustedStores.map((s) => (
              <Link
                key={s.user_id}
                to={`/magaza/${s.store?.slug}`}
                data-testid={`trusted-store-${s.store?.slug}`}
                className="group block p-6 rounded-xl bg-white hover:soft-shadow-lg transition-all duration-500 border border-slate-100"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-lg bg-slate-900 text-white flex items-center justify-center">
                    {s.store?.logo ? (
                      <img src={s.store.logo} alt={s.store.name} className="w-full h-full object-cover rounded-lg" />
                    ) : (
                      <Storefront size={20} weight="regular" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="font-display text-base font-medium tracking-tight text-slate-900 truncate">{s.store?.name}</p>
                    <p className="text-[10px] uppercase tracking-[0.22em] text-slate-400 mt-0.5">
                      {s.is_verified ? "Onaylı Mağaza" : "Mağaza"}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div>
                    <p className="font-mono-tight text-sm font-medium text-slate-900">{s.active_listings}</p>
                    <p className="text-[9px] uppercase tracking-[0.2em] text-slate-400 mt-1">ilan</p>
                  </div>
                  <div>
                    <p className="font-mono-tight text-sm font-medium text-slate-900">{s.store?.followers_count || 0}</p>
                    <p className="text-[9px] uppercase tracking-[0.2em] text-slate-400 mt-1">takipçi</p>
                  </div>
                  <div>
                    <p className="font-mono-tight text-sm font-medium text-slate-900">
                      {s.seller_rating > 0 ? `${s.seller_rating.toFixed(1)}★` : "—"}
                    </p>
                    <p className="text-[9px] uppercase tracking-[0.2em] text-slate-400 mt-1">
                      {s.review_count} puan
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <EmptyBlock
            testid="trusted-empty"
            icon={Storefront}
            title="İlk mağazalar yolda"
            copy="Kurumsal satıcı olarak sen de mağaza açabilir, güvenilir satıcılar arasında yer alabilirsin."
            cta="Mağaza kur"
            href="/magazam/ayarlar"
          />
        )}
      </Section>

      {/* ============ YENİ EKLENENLER ============ */}
      {newest.length > 0 && (
        <Section
          eyebrow="Taze ilanlar"
          title="Yeni eklenenler"
          testId="section-newest"
          right={<Link to="/kesfet?sort=newest" className="text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors">Hepsini gör →</Link>}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="home-newest-grid">
            {newest.slice(0, 4).map((a) => <AuctionCard key={a.id} auction={a} onFav={toggleFav} isFav={favSet.has(a.id)} />)}
          </div>
        </Section>
      )}

      {/* ============ NASIL ÇALIŞIR ============ */}
      <section className="max-w-[1280px] mx-auto px-6 py-24 md:py-28 border-t divider" data-testid="how-it-works">
        <div className="mb-16 max-w-2xl">
          <p className="text-[11px] uppercase tracking-[0.28em] font-medium text-slate-400 mb-3">Nasıl çalışır</p>
          <h2 className="font-display text-4xl md:text-5xl font-medium tracking-tight text-slate-900">
            Üç adımda ürününü sat.
          </h2>
          <p className="text-slate-500 mt-4 leading-relaxed">Karmaşık kayıt, gizli komisyon yok. Ürününü ekle, teklifleri topla, güvenle teslim et.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-16">
          {[
            { icon: Package, step: "01", title: "Listele", desc: "Ürününü ekle, başlangıç fiyatını ve süreni belirle. Fotoğraf çek, birkaç dakikada yayında." },
            { icon: TrendUp, step: "02", title: "Teklif topla", desc: "Socket.io altyapımızla tekliflerin saniyelik yükselişini izle. Bildirimler anlık." },
            { icon: ShieldCheck, step: "03", title: "Güvenle teslim et", desc: "Para havuz hesapta bekler, alıcı onaylayınca hesabına geçer. Param Güvende ile risksiz." },
          ].map((s) => (
            <div key={s.step} className="space-y-5">
              <div className="flex items-baseline gap-4">
                <span className="font-mono-tight text-[11px] tracking-[0.2em] text-slate-400">{s.step}</span>
                <span className="h-px flex-1 bg-slate-100" />
                <s.icon size={18} weight="regular" className="text-slate-400" />
              </div>
              <h3 className="font-display text-2xl font-medium tracking-tight text-slate-900">{s.title}</h3>
              <p className="text-slate-500 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ============ REHBER / BLOG ============ */}
      {blogs.length > 0 && (
        <Section
          eyebrow="Rehber"
          eyebrowIcon={<Article size={12} weight="regular" />}
          title="Alıcı ve satıcı için içerikler"
          testId="section-blogs"
          right={<Link to="/rehber" className="text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors">Tüm yazılar →</Link>}
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="home-blogs-grid">
            {blogs.map((b) => (
              <Link
                key={b.id}
                to={`/rehber/${b.slug}`}
                data-testid={`blog-tile-${b.slug}`}
                className="group block p-6 rounded-xl bg-white hover:soft-shadow-lg transition-all duration-500 border border-slate-100"
              >
                <p className="text-[10px] uppercase tracking-[0.22em] font-medium text-slate-400 mb-3">
                  {new Date(b.created_at).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" })}
                </p>
                <h3 className="font-display text-xl font-medium tracking-tight text-slate-900 leading-snug group-hover:text-slate-700 transition-colors mb-3">
                  {b.title}
                </h3>
                <p className="text-sm text-slate-500 leading-relaxed line-clamp-3">{b.excerpt}</p>
                <span className="inline-flex items-center gap-1 mt-4 text-xs font-medium text-slate-500 group-hover:text-slate-900 transition-colors">
                  Devamını oku <ArrowRight size={12} weight="bold" />
                </span>
              </Link>
            ))}
          </div>
        </Section>
      )}

      {/* ============ TRUST BAND ============ */}
      <section className="border-t divider" data-testid="trust-band">
        <div className="max-w-[1280px] mx-auto px-6 py-24 grid grid-cols-1 md:grid-cols-3 gap-16">
          {[
            { icon: ShieldCheck, title: "TCKN doğrulamalı üyeler", desc: "e-Devlet üzerinden kimlik doğrulama zorunlu." },
            { icon: HandCoins, title: "Havuz hesap güvencesi", desc: "Param Güvende altyapısıyla korumalı ödeme." },
            { icon: Headset, title: "7/24 canlı destek", desc: "İhtiyacın olduğunda yanındayız." },
          ].map((f) => (
            <div key={f.title} className="space-y-4">
              <f.icon size={22} weight="regular" className="text-slate-700" />
              <p className="font-display font-medium text-lg tracking-tight text-slate-900">{f.title}</p>
              <p className="text-sm text-slate-500 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer className="border-t divider">
        <div className="max-w-[1280px] mx-auto px-6 py-16 grid grid-cols-1 md:grid-cols-4 gap-12">
          <div>
            <p className="font-display text-xl font-medium text-slate-900 tracking-tight mb-3">kolaysatis<span className="text-[#B54037]">.</span>com</p>
            <p className="text-sm text-slate-500 leading-relaxed">Türkiye&apos;nin şeffaf canlı açık artırma platformu.</p>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-[0.24em] font-medium text-slate-400 mb-4">Alıcı</p>
            <ul className="space-y-3 text-sm">
              <li><Link to="/kesfet" className="text-slate-600 hover:text-slate-900 transition-colors">Açık artırmaları keşfet</Link></li>
              <li><Link to="/kesfet?listing_type=open_ended" className="text-slate-600 hover:text-slate-900 transition-colors">Hemen al</Link></li>
              <li><Link to="/dogrula" className="text-slate-600 hover:text-slate-900 transition-colors">Kimlik doğrulama</Link></li>
            </ul>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-[0.24em] font-medium text-slate-400 mb-4">Satıcı</p>
            <ul className="space-y-3 text-sm">
              <li><Link to="/ilan-ver" className="text-slate-600 hover:text-slate-900 transition-colors">İlan ver</Link></li>
              <li><Link to="/magazam/ayarlar" className="text-slate-600 hover:text-slate-900 transition-colors">Kurumsal mağaza</Link></li>
              <li><Link to="/panelim" className="text-slate-600 hover:text-slate-900 transition-colors">Panelim</Link></li>
            </ul>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-[0.24em] font-medium text-slate-400 mb-4">Teknoloji</p>
            <ul className="space-y-3 text-sm text-slate-600">
              <li className="flex items-center gap-2"><Lightning size={13} /> Socket.io canlı teklif</li>
              <li className="flex items-center gap-2"><ShieldCheck size={13} /> Stripe güvenli ödeme</li>
              <li className="flex items-center gap-2"><ClockCounterClockwise size={13} /> Otomatik kapanış</li>
            </ul>
          </div>
        </div>
        <div className="border-t divider py-6 flex flex-col md:flex-row items-center justify-center md:justify-between gap-3 max-w-[1280px] mx-auto px-6">
          <p className="text-xs text-slate-400">
            © {new Date().getFullYear()} kolaysatis.com · Tüm hakları saklıdır.
          </p>
          <nav className="flex flex-wrap gap-5 text-xs text-slate-500" data-testid="footer-legal">
            <Link to="/kullanim-sartlari" className="hover:text-slate-900 transition-colors" data-testid="footer-terms">Kullanım Şartları</Link>
            <Link to="/gizlilik" className="hover:text-slate-900 transition-colors" data-testid="footer-privacy">Gizlilik Politikası</Link>
            <Link to="/kvkk" className="hover:text-slate-900 transition-colors" data-testid="footer-kvkk">KVKK Aydınlatma</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}

function Section({ eyebrow, eyebrowIcon, eyebrowColor, title, right, testId, children }) {
  return (
    <section className="bg-white py-14 md:py-20 border-t divider" data-testid={testId}>
      <div className="max-w-[1280px] mx-auto px-6">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8 md:mb-12">
          <div>
            <p className={`text-[11px] uppercase tracking-[0.28em] font-medium ${eyebrowColor || "text-slate-400"} flex items-center gap-2 mb-3`}>
              {eyebrowIcon} {eyebrow}
            </p>
            <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight text-slate-900">
              {title}
            </h2>
          </div>
          {right}
        </div>
        {children}
      </div>
    </section>
  );
}

function EmptyBlock({ icon: Icon, title, copy, cta, href, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-2xl bg-slate-50 py-14 md:py-20 px-8 flex flex-col items-center justify-center text-center"
    >
      <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center mb-5 border border-slate-100">
        <Icon size={22} weight="regular" className="text-slate-500" />
      </div>
      <h3 className="font-display text-xl md:text-2xl font-medium tracking-tight text-slate-900 mb-3">
        {title}
      </h3>
      <p className="text-sm md:text-base text-slate-500 leading-relaxed max-w-md">{copy}</p>
      <Link
        to={href}
        className="mt-6 inline-flex items-center gap-1.5 bg-slate-900 text-white text-sm font-medium px-5 py-2.5 rounded-lg hover:bg-slate-800 transition-colors"
      >
        {cta} <ArrowRight size={13} weight="bold" />
      </Link>
    </div>
  );
}

function VitrinAdSlot({ idx }) {
  return (
    <Link
      to="/panelim"
      data-testid={`vitrin-ad-slot-${idx}`}
      className="group block rounded-xl overflow-hidden bg-slate-50 hover:bg-white hover:soft-shadow-lg transition-all duration-500 min-h-[380px] flex flex-col items-center justify-center p-6 text-center"
    >
      <Star size={22} weight="fill" className="text-amber-400 mb-4" />
      <p className="text-[10px] uppercase tracking-[0.28em] font-medium text-slate-400 mb-2">Reklam alanı</p>
      <p className="font-display text-lg font-medium text-slate-900 tracking-tight leading-tight mb-2">Burada yer alın</p>
      <p className="text-sm text-slate-500 leading-relaxed max-w-[240px]">
        İlanını doping ile öne çıkar, 5 kat daha fazla teklif al.
      </p>
      <span className="mt-4 text-xs font-medium text-slate-500 group-hover:text-slate-900 transition-colors">
        Doping paketleri →
      </span>
    </Link>
  );
}

function VitrinCard({ auction }) {
  const openEnded = auction.listing_type === "open_ended";
  const closed = auction.status !== "active";
  const urgent = auction.doping_type === "urgent_badge";
  const isPremium = auction.doping_type === "homepage_vitrin";
  const bold = auction.doping_type === "bold_title";
  const hot = auction.is_hot_auction && !closed;
  const fallback = "https://images.unsplash.com/photo-1611095973362-88e8e2557e58?w=800&q=80";
  const imgSrc = (p) => (!p ? fallback : p.startsWith("http") ? p : `${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`);

  return (
    <Link
      to={`/mezat/${auction.id}`}
      data-testid={`featured-card-${auction.id}`}
      className="group block bg-white rounded-xl overflow-hidden vitrin-glow hover:-translate-y-0.5 transition-all duration-500"
    >
      <div className="relative aspect-[4/3] bg-slate-50 overflow-hidden">
        <img
          src={imgSrc(auction.images?.[0])}
          alt={auction.title}
          className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-[900ms] ease-out"
          onError={(e) => { e.target.src = fallback; }}
        />
        <div className="absolute top-3 left-3">
          {urgent ? (
            <span className="inline-flex items-center gap-1 bg-[#B54037] text-white text-[10px] uppercase tracking-[0.22em] font-medium px-2.5 py-1 rounded-full">
              <Lightning size={10} weight="fill" /> Acil
            </span>
          ) : isPremium ? (
            <span className="inline-flex items-center gap-1 bg-slate-900 text-white text-[10px] uppercase tracking-[0.22em] font-medium px-2.5 py-1 rounded-full">
              <Star size={10} weight="fill" className="text-amber-400" /> Öne çıkan
            </span>
          ) : hot ? (
            <span data-testid={`vitrin-hot-badge-${auction.id}`}
              className="hot-badge inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] font-medium px-2.5 py-1 rounded-full">
              Kızışan
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 bg-white/95 backdrop-blur-sm text-slate-800 text-[10px] uppercase tracking-[0.22em] font-medium px-2.5 py-1 rounded-full premium-border">
              <Star size={10} weight="fill" className="text-amber-500" /> Vitrin
            </span>
          )}
        </div>
      </div>
      <div className="p-5 space-y-3">
        <p className="text-[10px] uppercase tracking-[0.22em] flex items-center justify-between text-slate-400">
          <span>{auction.category}</span>
          {auction.city && <span className="flex items-center gap-1 normal-case tracking-normal"><MapPin size={10} /> {auction.city}</span>}
        </p>
        <h3 className={`font-display leading-tight line-clamp-2 min-h-[3rem] tracking-tight ${bold ? "text-xl font-medium text-[#B54037]" : "text-xl font-medium text-slate-900"}`}>
          {auction.title}
        </h3>
        <div className="flex items-end justify-between pt-4 border-t divider">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-1">
              {openEnded ? "Hemen al" : "Mevcut"}
            </p>
            <p className="font-mono-tight text-xl font-medium text-slate-900">
              ₺{Number(openEnded ? (auction.buy_now_price || auction.current_price) : auction.current_price).toLocaleString("tr-TR")}
            </p>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-1">{auction.bid_count} teklif</p>
            <p className="font-mono-tight text-xs text-[#B54037]">
              {openEnded ? "Süresiz" : <RelativeTime endTime={auction.end_time} />}
            </p>
          </div>
        </div>
      </div>
    </Link>
  );
}

function RelativeTime({ endTime }) {
  const [text, setText] = useState("");
  useEffect(() => {
    const upd = () => {
      const ms = new Date(endTime).getTime() - Date.now();
      if (ms <= 0) return setText("Sona erdi");
      const s = Math.floor(ms / 1000);
      const d = Math.floor(s / 86400);
      const h = Math.floor((s % 86400) / 3600);
      const m = Math.floor((s % 3600) / 60);
      const sec = s % 60;
      if (d > 0) setText(`${d}g ${h}sa`);
      else setText(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`);
    };
    upd();
    const id = setInterval(upd, 1000);
    return () => clearInterval(id);
  }, [endTime]);
  return <span>{text}</span>;
}
